import * as React from "react"

export const MOBILE_BREAKPOINT = 768
export const MOBILE_QUERY = `(max-width: ${MOBILE_BREAKPOINT - 1}px)`

const subscribe = (onChange: () => void) => {
  const mql = window.matchMedia(MOBILE_QUERY)
  mql.addEventListener("change", onChange)
  return () => mql.removeEventListener("change", onChange)
}

const getSnapshot = () => window.matchMedia(MOBILE_QUERY).matches

/**
 * True below the md breakpoint (phones). Read synchronously, so the first
 * render already picks the phone layout instead of flashing the desktop one.
 */
export function useIsMobile() {
  return React.useSyncExternalStore(subscribe, getSnapshot, () => false)
}

export const isMobileViewport = () =>
  typeof window !== "undefined" && getSnapshot()

/**
 * Phones at any width, including landscape, and tablets: touch is the main
 * input and an on-screen keyboard covers the page.
 */
export const isTouchViewport = () =>
  typeof window !== "undefined" &&
  (getSnapshot() || window.matchMedia("(pointer: coarse)").matches)

/**
 * Passes `isMobile` to a class component (Messages, ChatBox…), re-rendering
 * it when the phone breakpoint is crossed.
 */
export function withIsMobile<P extends { isMobile?: boolean }>(Component: React.ComponentType<P>) {
  const WithIsMobile = React.forwardRef<unknown, Omit<P, "isMobile">>((props, ref) => {
    const isMobile = useIsMobile()
    return <Component {...(props as unknown as P)} ref={ref} isMobile={isMobile} />
  })
  WithIsMobile.displayName = `withIsMobile(${Component.displayName || Component.name || "Component"})`
  return WithIsMobile
}
